import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faSignOutAlt, faLifeRing, faUser, faUserCheck, faExclamationCircle, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../../context/AuthContext';
import styles from './AdminHeader.module.css';

// ✅ ADD THIS (same as your working DashboardPage):
const API_BASE_URL = "https://final-project-5ar7.onrender.com";

console.log('🌐 ADMIN HEADER API_BASE_URL:', API_BASE_URL);

function AdminHeader({ onSupportTicketClick }) {
  // Function starts here...

  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [notifications, setNotifications] = useState({
    userApprovals: 0,
    supportTickets: 0
  });
  
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // ✅ Fetch real notification counts AND actual data
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // ✅ FIXED: Add authentication headers and use correct endpoint
      const token = localStorage.getItem("userToken");
      
      const pendingResponse = await fetch(`${API_BASE_URL}/api/admin/public/pending-users`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        }
      });
      const pendingData = await pendingResponse.json();
      
      const supportResponse = await fetch(`${API_BASE_URL}/api/support/tickets/count`);
      const supportData = await supportResponse.json();
      
      // ✅ FIXED: Handle array response directly (not nested in success object)
      const pendingUsersList = Array.isArray(pendingData) ? pendingData : [];
      
      setNotifications({
        userApprovals: pendingUsersList.length,
        supportTickets: supportData.success ? supportData.count : 0
      });
      
      setPendingUsers(pendingUsersList);
      
      console.log('✅ Notifications updated:', {
        userApprovals: pendingUsersList.length,
        supportTickets: supportData.success ? supportData.count : 0,
        pendingUsers: pendingUsersList
      });
      
    } catch (error) {
      console.error('❌ Failed to fetch notifications:', error);
      // Keep default values if API fails
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // ✅ DISABLED: No more auto-refresh to prevent loops
    // const interval = setInterval(fetchNotifications, 30000);
    // return () => clearInterval(interval);
  }, []);

  // ✅ Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ✅ Handle bell notification click
  const handleNotificationClick = () => {
    setShowNotificationDropdown(!showNotificationDropdown);
  };

  // ✅ Handle support ticket click
  const handleSupportTicketClick = () => {
    if (onSupportTicketClick) {
      onSupportTicketClick();
    }
  };

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationDropdown && !event.target.closest('.notificationContainer')) {
        setShowNotificationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown]);

  return (
    <header className={styles.header}>
      <div className={styles.leftSection}>
        <h1 className={styles.pageTitle}>Admin Dashboard</h1>
        <p className={styles.date}>{currentDate}</p>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.notifications}>
          
          {/* ✅ User Approvals Notification with Dropdown */}
          <div className={`${styles.notificationContainer} notificationContainer`}>
            <button 
              className={styles.notificationBtn} 
              title="User Approvals & Account Requests"
              onClick={handleNotificationClick}
            >
              <FontAwesomeIcon icon={faBell} />
              {notifications.userApprovals > 0 && (
                <span className={styles.badge}>
                  {notifications.userApprovals}
                </span>
              )}
            </button>
            
            {/* ✅ Notification Dropdown */}
            {showNotificationDropdown && (
              <div className={styles.notificationDropdown}>
                <div className={styles.dropdownHeader}>
                  <h4>Pending User Approvals</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className={styles.count}>{notifications.userApprovals} pending</span>
                    {/* ✅ MANUAL REFRESH BUTTON IN DROPDOWN */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchNotifications();
                      }}
                      disabled={loading}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3b82f6',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        padding: '2px'
                      }}
                      title="Refresh notifications"
                    >
                      <FontAwesomeIcon icon={faRefresh} spin={loading} size="sm" />
                    </button>
                  </div>
                </div>
                
                <div className={styles.dropdownContent}>
                  {pendingUsers.length > 0 ? (
                    pendingUsers.slice(0, 5).map((user, index) => (
                      <div key={user._id || index} className={styles.notificationItem}>
                        <div className={styles.notificationIcon}>
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div className={styles.notificationInfo}>
                          <p className={styles.notificationTitle}>
                            {user.firstName} {user.lastName}
                          </p>
                          <p className={styles.notificationDesc}>
                            New user registration - {user.email}
                          </p>
                          <p className={styles.notificationTime}>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.noNotifications}>
                      <FontAwesomeIcon icon={faUserCheck} />
                      <p>All users approved!</p>
                    </div>
                  )}
                  
                  {pendingUsers.length > 5 && (
                    <div className={styles.viewMore}>
                      <p>+{pendingUsers.length - 5} more pending approvals</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* ✅ Support Ticket Notification */}
          <button 
            className={`${styles.notificationBtn} ${styles.supportBtn}`} 
            title="Support Tickets"
            onClick={handleSupportTicketClick}
          >
            <FontAwesomeIcon icon={faLifeRing} />
            {notifications.supportTickets > 0 && (
              <span className={`${styles.badge} ${styles.supportBadge}`}>
                {notifications.supportTickets}
              </span>
            )}
          </button>
        </div>

        <div className={styles.userMenu}>
          <div className={styles.userInfo}>
            <span className={styles.userName}>Admin</span>
            <span className={styles.userRole}>System Administrator</span>
          </div>
          
          {/* ✅ Logout button with functionality */}
          <button 
            className={styles.logoutBtn} 
            title="Logout and return to homepage"
            onClick={handleLogout}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      </div>
    </header>
  );
}

export default AdminHeader;
